import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { StatCard } from "../components/StatCard";
import { CategoryPie } from "../components/CategoryPie";
import { useCategories } from "../lib/useCategories";
import { formatCurrency, formatDate } from "../lib/utils-image";
import {
  Wallet,
  TrendingDown,
  CircleDollarSign,
  ReceiptText,
  Calendar,
  Plus,
  ArrowLeft,
  Sparkles,
  AlertTriangle,
  Archive,
} from "lucide-react";

const MONTH_NAMES = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

export default function DashboardPage() {
  useCategories();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retention, setRetention] = useState(null);
  const [anomalies, setAnomalies] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
    api.get("/invoices/retention/summary")
      .then((r) => setRetention(r.data))
      .catch(() => setRetention(null));
    api.get("/invoices/anomalies")
      .then((r) => setAnomalies(r.data))
      .catch(() => setAnomalies(null));
  }, []);

  const currency = stats?.currency || "SAR";
  const monthOpening = stats?.month_opening_balance || 0;
  const monthOpeningBase = stats?.month_opening_base ?? stats?.opening_balance ?? 0;
  const monthSupport = stats?.month_support_total || 0;
  const monthCarryOver = stats?.month_carry_over || 0;
  const monthSpent = stats?.month_expense || 0;
  const monthRemaining = stats?.month_remaining_balance || 0;
  const overallRemaining = stats?.remaining_balance || 0;
  const totalExpense = stats?.total_expense || 0;
  const monthName = stats?.current_month ? MONTH_NAMES[stats.current_month - 1] : "";
  const spentPercent = monthOpening > 0 ? Math.min(100, (monthSpent / monthOpening) * 100) : 0;
  const isHealthy = monthRemaining >= 0 && spentPercent < 80;

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">لوحة التحكم</div>
          <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">
            نظرة عامة على حسابك
          </h1>
          <p className="text-[#5C605A] mt-2 text-sm sm:text-base">
            تابع رصيدك ومصروفاتك بدقة، أضف فاتورة بضغطة واحدة.
          </p>
        </div>
        <Link
          to="/add"
          data-testid="dashboard-add-btn"
          className="hidden sm:inline-flex bg-[#1F3A33] text-white hover:bg-[#172A25] active:translate-y-[1px] transition-all rounded-md px-5 py-3 font-medium font-readex items-center gap-2 self-start sm:self-auto shadow-[0_2px_8px_rgba(31,58,51,0.18)] hover:shadow-[0_6px_16px_rgba(31,58,51,0.25)]"
        >
          <Plus className="w-5 h-5" strokeWidth={1.5} />
          إضافة فاتورة
        </Link>
      </div>

      {/* Retention notice — show only if there are expiring/expired invoices */}
      {retention && (retention.expiring_soon > 0 || retention.expired > 0) && (
        <div
          className="rounded-md border border-[#E8B96E]/40 bg-[#FFF6E3] p-4 sm:p-5 flex items-start gap-3 fade-up"
          data-testid="retention-banner"
        >
          <div className="w-10 h-10 rounded-md bg-[#E8B96E]/30 text-[#7A4D00] flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-readex font-medium text-[#7A4D00] mb-1">تنبيه: سياسة الاحتفاظ بالفواتير</div>
            <p className="text-sm text-[#5C4520] leading-relaxed">
              {retention.expiring_soon > 0 && (
                <span>
                  لديك <strong className="font-mono-num">{retention.expiring_soon}</strong> فاتورة ستنتهي مدة الاحتفاظ بها خلال 30 يوماً.
                </span>
              )}
              {retention.expired > 0 && (
                <span className="block mt-1">
                  لديك <strong className="font-mono-num">{retention.expired}</strong> فاتورة تجاوزت مدة الاحتفاظ (سنة كاملة) — يُنصح بأرشفتها أو تصديرها.
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Link
                to="/invoices?filter=expiring"
                data-testid="retention-view-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white text-[#7A4D00] border border-[#E8B96E]/50 text-xs font-readex hover:bg-[#FFF1D0]"
              >
                <Archive className="w-3.5 h-3.5" /> عرض الفواتير المنتهية
              </Link>
              <Link
                to="/reports"
                data-testid="retention-export-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#1F3A33] text-white text-xs font-readex hover:bg-[#172A25]"
              >
                تصدير PDF / Excel
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hero balance card */}
      {!loading && stats && (
        <div className="relative hero-3d rounded-md p-6 sm:p-8 text-white overflow-hidden fade-up shadow-3d-emerald">
          <div className="absolute inset-0 brand-stripes opacity-40" aria-hidden="true" />
          {/* Decorative floating orbs for 3D feel */}
          <div className="absolute -bottom-20 -end-20 w-56 h-56 rounded-full bg-[#6A8A76]/25 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="absolute -top-16 -start-12 w-40 h-40 rounded-full bg-[#C84C31]/12 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" aria-hidden="true" />

          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-6 items-end">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#A3C4B0]" strokeWidth={1.5} />
                <div className="text-xs uppercase tracking-wider text-white/70 font-readex">
                  متبقي {monthName} {stats.current_year}
                </div>
              </div>
              <div
                key={String(monthRemaining)}
                className={`font-readex font-medium text-4xl sm:text-5xl tracking-tight font-mono-num tick-in ${monthRemaining < 0 ? "text-[#FFB4A2]" : ""}`}
                data-testid="hero-remaining"
              >
                {formatCurrency(monthRemaining, currency)}
              </div>

              {/* Balance breakdown: base opening + carry-over = monthly start */}
              <div className="mt-4 space-y-1.5 text-xs font-mono-num" data-testid="balance-breakdown">
                <div className="flex items-center justify-between gap-3 text-white/75">
                  <span className="font-readex text-white/70">الرصيد الافتتاحي (يدوي)</span>
                  <span data-testid="balance-base">{formatCurrency(monthOpeningBase, currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-white/75">
                  <span className="font-readex text-white/70">المرحّل من الشهر السابق</span>
                  <span
                    className={monthCarryOver < 0 ? "text-[#FFB4A2]" : monthCarryOver > 0 ? "text-[#A3E4B5]" : "text-white/60"}
                    data-testid="balance-carry-over"
                  >
                    {monthCarryOver > 0 ? "+" : ""}{formatCurrency(monthCarryOver, currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 text-white/75">
                  <span className="font-readex text-white/70">الدعم الإضافي</span>
                  <span data-testid="balance-support">{formatCurrency(monthSupport, currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-white/75">
                  <span className="font-readex text-white/70">إجمالي تمويل الشهر</span>
                  <span>{formatCurrency(monthOpeningBase + monthSupport, currency)}</span>
                </div>
                <div className="h-px bg-white/15 my-1" />
                <div className="flex items-center justify-between gap-3 text-white">
                  <span className="font-readex font-medium">إجمالي الرصيد المتاح</span>
                  <span className="font-medium" data-testid="balance-month-opening">{formatCurrency(monthOpening, currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-white/60">
                  <span className="font-readex">إنفاق هذا الشهر</span>
                  <span>−{formatCurrency(monthSpent, currency)}</span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between text-xs text-white/70 mb-2 font-readex">
                <span>تم إنفاق {spentPercent.toFixed(1)}% من رصيد الشهر</span>
                <span className="font-mono-num">{stats.invoice_count} إجمالي</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${spentPercent}%`,
                    backgroundColor: isHealthy ? "#6A8A76" : "#E89178",
                  }}
                />
              </div>
              <div className="text-xs text-white/60 mt-3 leading-relaxed">
                {isHealthy
                  ? `وضع مالي صحي لشهر ${monthName}.`
                  : monthRemaining < 0
                  ? `الرصيد سالب هذا الشهر — سيُرحَّل إلى الشهر التالي.`
                  : `تنبيه: نسبة إنفاق مرتفعة في ${monthName}.`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Anomaly alert: invoices with out-of-range dates poison the carry-over */}
      {anomalies && anomalies.count > 0 && (
        <div
          className="bg-[#FBE9E3] border border-[#E89178] rounded-md p-4 sm:p-5 flex items-start gap-3"
          data-testid="anomaly-banner"
        >
          <AlertTriangle className="w-5 h-5 text-[#C84C31] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <div className="font-readex font-medium text-[#1A1C19] mb-1">
              تنبيه: تواريخ فواتير غير منطقية
            </div>
            <p className="text-sm text-[#5C605A] leading-relaxed">
              لديك <strong className="text-[#C84C31] font-mono-num">{anomalies.count}</strong>{" "}
              فاتورة بتاريخ خارج النطاق المعقول ({anomalies.min_year}–{anomalies.max_year}).
              تؤدّي هذه الفواتير إلى ترحيل أرقام غير صحيحة بين الأشهر.
              <br />
              {anomalies.items?.[0] && (
                <span className="text-xs text-[#8A8D86]">
                  مثال: «{anomalies.items[0].store_name || "—"}» بتاريخ {anomalies.items[0].invoice_date}{" "}
                  بقيمة {formatCurrency(anomalies.items[0].total_amount || 0, currency)}.
                </span>
              )}
            </p>
            <Link
              to="/invoices?year_lt=2024"
              data-testid="anomaly-fix-link"
              className="inline-flex items-center gap-1.5 mt-3 text-[#C84C31] hover:text-[#8B3621] text-sm font-medium font-readex underline underline-offset-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
              مراجعة الفواتير وتصحيح التواريخ
            </Link>
          </div>
        </div>
      )}

      {/* Stat cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5 perspective-1000">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-36 rounded-md shimmer" />
          ))
        ) : (
          <>
            <StatCard
              testid="stat-opening-balance"
              label={`الرصيد المتاح في ${monthName}`}
              value={formatCurrency(monthOpening, currency)}
              hint={
                monthCarryOver !== 0
                  ? `${formatCurrency(monthOpeningBase, currency)} + ${monthCarryOver > 0 ? "+" : ""}${formatCurrency(monthCarryOver, currency)} مرحّل`
                  : null
              }
              icon={Wallet}
            />
            <StatCard
              testid="stat-total-expense"
              label={`مصروفات ${monthName}`}
              value={formatCurrency(monthSpent, currency)}
              icon={TrendingDown}
              tone="danger"
            />
            <StatCard
              testid="stat-remaining-balance"
              label={`متبقي ${monthName}`}
              value={formatCurrency(monthRemaining, currency)}
              icon={CircleDollarSign}
              tone={monthRemaining >= 0 ? "success" : "danger"}
            />
            <StatCard
              testid="stat-invoice-count"
              label="إجمالي الفواتير"
              value={String(stats?.invoice_count || 0)}
              icon={ReceiptText}
            />
            <StatCard
              testid="stat-overall-remaining"
              label="الرصيد التراكمي"
              value={formatCurrency(overallRemaining, currency)}
              hint={`إجمالي إنفاق: ${formatCurrency(totalExpense, currency)}`}
              icon={Calendar}
              tone={overallRemaining >= 0 ? "muted" : "danger"}
            />
          </>
        )}
      </div>

      {/* Category breakdown pie — current month */}
      {!loading && (
        <CategoryPie
          data={stats?.category_breakdown || []}
          currency={currency}
          title={`مصاريف ${monthName} ${stats?.current_year || ""}`}
          subtitle="حسب صنف الفاتورة"
          testid="dashboard-category-pie"
        />
      )}

      {/* Latest invoices */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-md">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-[#1A1C19]/10">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-1 font-readex">أحدث الفواتير</div>
            <h2 className="font-readex font-medium text-xl text-[#1A1C19]">آخر العمليات</h2>
          </div>
          <Link
            to="/invoices"
            data-testid="dashboard-view-all-btn"
            className="text-sm font-medium text-[#1F3A33] hover:underline inline-flex items-center gap-1 font-readex"
          >
            عرض الكل
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 shimmer rounded-md" />
            ))}
          </div>
        ) : (stats?.latest_invoices?.length || 0) === 0 ? (
          <div className="p-10 text-center text-[#5C605A]">
            <ReceiptText className="w-10 h-10 mx-auto mb-3 text-[#8A8D86]" strokeWidth={1.5} />
            <p className="mb-4">لا توجد فواتير بعد. ابدأ بإضافة أول فاتورة.</p>
            <Link
              to="/add"
              className="inline-flex bg-[#1F3A33] text-white hover:bg-[#172A25] transition-colors rounded-md px-5 py-2.5 font-medium font-readex"
            >
              <Plus className="w-4 h-4 me-2" strokeWidth={1.5} />
              إضافة فاتورة
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#1A1C19]/5">
            {stats.latest_invoices.map((inv, idx) => (
              <li
                key={inv.id}
                className="flex items-center justify-between p-4 sm:p-5 hover:bg-[#F5F4EF]/50 transition-colors"
                data-testid={`latest-invoice-${idx}`}
              >
                <div className="min-w-0">
                  <div className="font-readex font-medium text-[#1A1C19] truncate">
                    {inv.store_name || "—"}
                  </div>
                  <div className="text-xs text-[#8A8D86] mt-0.5">
                    {formatDate(inv.invoice_date)}
                    {inv.invoice_time ? ` · ${inv.invoice_time}` : ""}
                  </div>
                </div>
                <div className="text-end shrink-0 ms-4">
                  <div className="font-mono-num font-medium text-[#C84C31]">
                    − {formatCurrency(inv.total_amount, currency)}
                  </div>
                  <div className="text-xs text-[#8A8D86] mt-0.5 font-mono-num">
                    {formatCurrency(inv.balance_after, currency)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

